import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { Exercise, WorkoutBlock, WorkoutWithBlocks } from '../../../../types/dataModel';
import { getWorkoutDisplayBannerUrl, isAllowlistedWorkoutBannerUrl } from '../../../../lib/workoutBannerImage';
import { WorkoutService, parseUpsertTrainerWorkoutError } from '../../../../lib/workoutService';
import { pendingBlockShowsAddExerciseButton } from '../../../../utils/blockSetupValidation';
import {
  appendExerciseRuns,
  areSetsUniform,
  cloneWorkout,
  computeExerciseRuns,
  countDirtyChanges,
  createBlockFromModalInput,
  formatDurationInputWhileTyping,
  formatSecondsToMMSS,
  hasUnsavedChanges,
  newExerciseGroupId,
  normalizeRepsTextOnBlur,
  parseDurationInputToSeconds,
  pruneExerciseMeasureTypesForBlock,
  REPS_TEXT_FINAL_PATTERN,
  rowGroupKeyForExerciseRun,
  runMeasureKey,
  sanitizeRepsTextInput,
  createEmptyWorkoutDraft,
  sanitizeWorkoutForSave,
  syncExerciseRunsToBlockSetCount,
  workoutPersistedMatchesPayload,
} from '../utils/trainerWorkoutEditUtils';
import { messageFromUnknownError } from '../../../../utils/unknownErrorMessage';

type TimeFieldKind = 'duration' | 'rest';

function timeFieldDraftKey(kind: TimeFieldKind, blockIndex: number, setIndex: number): string {
  return `${kind}:${blockIndex}:${setIndex}`;
}

export type UseTrainerWorkoutEditStateParams = {
  workout: WorkoutWithBlocks | null;
  mode?: 'edit' | 'create';
  onSaved: (result: WorkoutWithBlocks) => void | Promise<void>;
  onCreated?: (result: WorkoutWithBlocks) => void | Promise<void>;
  checkWorkoutNameExists?: (name: string, excludeWorkoutId?: string) => boolean;
};

export function useTrainerWorkoutEditState({
  workout,
  mode = 'edit',
  onSaved,
  onCreated,
  checkWorkoutNameExists,
}: UseTrainerWorkoutEditStateParams) {
  const [editedWorkout, setEditedWorkout] = useState<WorkoutWithBlocks | null>(null);
  const [initialSnapshot, setInitialSnapshot] = useState<WorkoutWithBlocks | null>(null);
  const [blockSets, setBlockSets] = useState<Record<string, number | string>>({});
  const [exerciseMeasureType, setExerciseMeasureType] = useState<Record<string, 'reps' | 'duration'>>({});
  /** Preserves rep/repText per set when toggling to duration so switching back restores values. */
  const [stashedRepsByRun, setStashedRepsByRun] = useState<
    Record<string, Array<{ rep?: number; repText?: string }>>
  >({});
  /** Preserves durationSeconds per set when toggling to reps (sync ref avoids stale reads in toggle). */
  const stashedDurationByRunRef = useRef<Record<string, (number | null | undefined)[]>>({});
  /** Raw MM:SS text while typing (avoids controlled reformat on each keystroke). */
  const [timeFieldDrafts, setTimeFieldDrafts] = useState<Record<string, string>>({});
  const [pendingBlockSetupIds, setPendingBlockSetupIds] = useState<Set<string>>(new Set());
  const [blockTypeLockedIds, setBlockTypeLockedIds] = useState<Set<string>>(new Set());
  const [expandedRepRuns, setExpandedRepRuns] = useState<Set<string>>(new Set());
  /** User chose "Same across sets" on a run that would otherwise show per-set rows. */
  const [forcedSameRuns, setForcedSameRuns] = useState<Set<string>>(new Set());
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<{ title: string; message: string } | null>(null);
  const [nameError, setNameErrorState] = useState<string | null>(null);
  const [nameErrorSeq, setNameErrorSeq] = useState(0);

  const setNameError = useCallback((message: string | null) => {
    setNameErrorState(message);
    if (message) setNameErrorSeq((n) => n + 1);
  }, []);

  useEffect(() => {
    if (mode === 'create') {
      const empty = createEmptyWorkoutDraft();
      setEditedWorkout(cloneWorkout(empty));
      setInitialSnapshot(cloneWorkout(empty));
      setBlockSets({});
      setPendingBlockSetupIds(new Set());
      setBlockTypeLockedIds(new Set());
      setExerciseMeasureType({});
      setStashedRepsByRun({});
      stashedDurationByRunRef.current = {};
      setTimeFieldDrafts({});
      setExpandedRepRuns(new Set());
      setForcedSameRuns(new Set());
      setSaveError(null);
      setNameError(null);
      return;
    }
    if (!workout) {
      setEditedWorkout(null);
      setInitialSnapshot(null);
      return;
    }
    const cloned = cloneWorkout(workout);
    cloned.bannerImageUrl = getWorkoutDisplayBannerUrl({
      id: cloned.id,
      bannerImageUrl: cloned.bannerImageUrl,
    });
    const snapshot = cloneWorkout(cloned);
    setEditedWorkout(cloned);
    setInitialSnapshot(snapshot);
    const bs: Record<string, number | string> = {};
    (cloned.workoutBlocks ?? []).forEach((b, i) => {
      const id = b.id || `${i}`;
      const tl = String(b.type || '').toLowerCase();
      if (tl === 'amrap') {
        bs[id] = b.sets != null && Number(b.sets) >= 1 ? Number(b.sets) : '';
      } else {
        bs[id] = b.sets != null && Number(b.sets) >= 1 ? Number(b.sets) : 1;
      }
    });
    setBlockSets(bs);
    setPendingBlockSetupIds(new Set());
    setBlockTypeLockedIds(new Set((cloned.workoutBlocks ?? []).map((b) => b.id).filter(Boolean) as string[]));
    setExerciseMeasureType({});
    setStashedRepsByRun({});
    stashedDurationByRunRef.current = {};
    setTimeFieldDrafts({});
    setExpandedRepRuns(new Set());
    setForcedSameRuns(new Set());
    setSaveError(null);
    setNameError(null);
  }, [mode, workout?.id]);

  const dirtyCount = useMemo(
    () => countDirtyChanges(initialSnapshot, editedWorkout, pendingBlockSetupIds),
    [initialSnapshot, editedWorkout, pendingBlockSetupIds],
  );

  const isDirty = dirtyCount > 0;

  const discard = useCallback(() => {
    if (!initialSnapshot) return;
    setEditedWorkout(cloneWorkout(initialSnapshot));
    const bs: Record<string, number | string> = {};
    (initialSnapshot.workoutBlocks ?? []).forEach((b, i) => {
      const id = b.id || `${i}`;
      bs[id] = b.sets != null && Number(b.sets) >= 1 ? Number(b.sets) : 1;
    });
    setBlockSets(bs);
    setPendingBlockSetupIds(new Set());
    setExerciseMeasureType({});
    setStashedRepsByRun({});
    stashedDurationByRunRef.current = {};
    setTimeFieldDrafts({});
    setExpandedRepRuns(new Set());
    setForcedSameRuns(new Set());
    setSaveError(null);
    setNameError(null);
  }, [initialSnapshot]);

  const updateField = useCallback(<K extends keyof WorkoutWithBlocks>(key: K, value: WorkoutWithBlocks[K]) => {
    if (key === 'name') setNameError(null);
    setEditedWorkout((prev) => (prev ? { ...prev, [key]: value } : prev));
  }, []);

  const updateBlock = useCallback((blockIndex: number, patch: Partial<WorkoutBlock>) => {
    setEditedWorkout((prev) => {
      if (!prev) return prev;
      const blocks = [...(prev.workoutBlocks ?? [])];
      blocks[blockIndex] = { ...blocks[blockIndex], ...patch };
      return { ...prev, workoutBlocks: blocks };
    });
  }, []);

  const updateBlockSetsUi = useCallback((blockId: string, value: number | string) => {
    setBlockSets((prev) => ({ ...prev, [blockId]: value }));
  }, []);

  const updateBlockSetsCount = useCallback((blockIndex: number, sets: number | string) => {
    setEditedWorkout((prev) => {
      if (!prev) return prev;
      const updated = cloneWorkout(prev);
      const block = updated.workoutBlocks[blockIndex];
      if (!block) return prev;
      const blockId = block.id || `${blockIndex}`;
      const typeLower = String(block.type || '').toLowerCase();
      const parsed = typeof sets === 'string' ? (sets === '' ? 1 : parseInt(sets, 10) || 1) : sets;
      const setsValue =
        typeLower === 'circuit' || typeLower === 'superset'
          ? Math.max(1, Math.min(20, Math.floor(Number(parsed)) || 1))
          : Math.max(1, Math.floor(Number(parsed)) || 1);
      setBlockSets((p) => ({ ...p, [blockId]: setsValue }));
      block.sets = setsValue;
      if (typeLower === 'circuit' || typeLower === 'superset') {
        syncExerciseRunsToBlockSetCount(block, setsValue);
        setExerciseMeasureType((prevM) =>
          pruneExerciseMeasureTypesForBlock(prevM, blockIndex),
        );
      }
      return updated;
    });
  }, []);

  const updateBlockNote = useCallback((blockIndex: number, note: string) => {
    updateBlock(blockIndex, { note });
  }, [updateBlock]);

  const updateBlockType = useCallback((blockIndex: number, newType: string) => {
    setEditedWorkout((prev) => {
      if (!prev) return prev;
      const updated = cloneWorkout(prev);
      const block = updated.workoutBlocks[blockIndex];
      if (!block) return prev;
      const blockId = block.id || `${blockIndex}`;
      if (blockTypeLockedIds.has(blockId)) return prev;

      const prevLower = String(block.type || '').toLowerCase();
      block.type = newType as WorkoutBlock['type'];
      if (newType === 'AMRAP') {
        if (prevLower !== 'amrap') {
          (block as { sets?: number }).sets = undefined;
          setBlockSets((p) => ({ ...p, [blockId]: '' }));
        }
      } else {
        if (!block.sets || block.sets < 1) block.sets = 1;
        const newLower = newType.toLowerCase();
        if (newLower === 'circuit' || newLower === 'superset') {
          const n = Math.max(1, Math.min(20, Math.floor(Number(block.sets)) || 1));
          block.sets = n;
          syncExerciseRunsToBlockSetCount(block, n);
        }
      }
      return updated;
    });
  }, [blockTypeLockedIds]);

  const duplicateBlock = useCallback((blockIndex: number) => {
    setEditedWorkout((prev) => {
      if (!prev) return prev;
      const updated = cloneWorkout(prev);
      const src = updated.workoutBlocks[blockIndex];
      if (!src) return prev;
      const dup = JSON.parse(JSON.stringify(src)) as WorkoutBlock & { exerciseSets?: any[] };
      dup.id = `temp-${Date.now()}`;
      dup.name = `${src.name || `Block ${blockIndex + 1}`} (Copy)`;
      if (dup.exerciseSets) {
        dup.exerciseSets = dup.exerciseSets.map((s: { id?: string }) => ({
          ...s,
          id: `temp-${Date.now()}-${Math.random()}`,
        }));
      }
      updated.workoutBlocks.splice(blockIndex + 1, 0, dup as (typeof updated.workoutBlocks)[number]);
      setBlockTypeLockedIds((p) => new Set(p).add(dup.id!));
      return updated;
    });
  }, []);

  const deleteBlock = useCallback((blockIndex: number) => {
    setEditedWorkout((prev) => {
      if (!prev) return prev;
      const updated = cloneWorkout(prev);
      const removed = updated.workoutBlocks[blockIndex];
      updated.workoutBlocks.splice(blockIndex, 1);
      if (removed?.id) {
        setPendingBlockSetupIds((p) => {
          const n = new Set(p);
          n.delete(removed.id!);
          return n;
        });
      }
      return updated;
    });
  }, []);

  const addBlockFromModal = useCallback((name: string, structureDb: string) => {
    setEditedWorkout((prev) => {
      if (!prev) return prev;
      const updated = cloneWorkout(prev);
      const block = createBlockFromModalInput(name, structureDb);
      if (!updated.workoutBlocks) updated.workoutBlocks = [];
      updated.workoutBlocks.push(block as (typeof updated.workoutBlocks)[number]);
      const id = block.id!;
      setBlockSets((p) => ({ ...p, [id]: block.sets ?? 1 }));
      setBlockTypeLockedIds((prevL) => new Set(prevL).add(id));
      if (!block.exerciseSets?.length) {
        setPendingBlockSetupIds((prevP) => new Set(prevP).add(id));
      }
      return updated;
    });
  }, []);

  const addExercisesToBlock = useCallback((blockIndex: number, exercises: Exercise[]) => {
    if (!exercises.length) return;
    setEditedWorkout((prev) => {
      if (!prev) return prev;
      const updated = cloneWorkout(prev);
      const block = updated.workoutBlocks[blockIndex];
      if (!block) return prev;
      appendExerciseRuns(block, blockIndex, exercises);
      const bid = block.id || `${blockIndex}`;
      setPendingBlockSetupIds((p) => {
        const n = new Set(p);
        n.delete(bid);
        return n;
      });
      setBlockTypeLockedIds((p) => new Set(p).add(bid));
      return updated;
    });
  }, []);

  const deleteExerciseRun = useCallback((blockIndex: number, groupKey: string) => {
    setEditedWorkout((prev) => {
      if (!prev) return prev;
      const updated = cloneWorkout(prev);
      const block = updated.workoutBlocks[blockIndex];
      if (!block?.exerciseSets) return prev;
      const runs = computeExerciseRuns(block.exerciseSets);
      const run = runs.find((r) => r.groupKey === groupKey);
      if (!run) return prev;
      block.exerciseSets.splice(run.firstIndex, run.indices.length);
      block.exerciseSets.forEach((s, i) => {
        s.order = i;
      });
      return updated;
    });
  }, []);

  const duplicateExerciseRun = useCallback((blockIndex: number, groupKey: string) => {
    setEditedWorkout((prev) => {
      if (!prev) return prev;
      const updated = cloneWorkout(prev);
      const block = updated.workoutBlocks[blockIndex];
      if (!block?.exerciseSets) return prev;
      const runs = computeExerciseRuns(block.exerciseSets);
      const run = runs.find((r) => r.groupKey === groupKey);
      if (!run) return prev;
      const newGroupId = newExerciseGroupId();
      const clones = run.sets.map((s: any) => {
        const c = JSON.parse(JSON.stringify(s));
        c.id = `temp-${Date.now()}-${Math.random()}`;
        c.exerciseGroupId = newGroupId;
        return c;
      });
      block.exerciseSets.splice(run.lastIndex + 1, 0, ...clones);
      block.exerciseSets.forEach((s, i) => {
        s.order = i;
      });
      return updated;
    });
  }, []);

  const reorderBlocks = useCallback((fromIndex: number, toIndex: number) => {
    setEditedWorkout((prev) => {
      if (!prev) return prev;
      const updated = cloneWorkout(prev);
      const blocks = updated.workoutBlocks;
      if (fromIndex < 0 || toIndex < 0 || fromIndex >= blocks.length || toIndex >= blocks.length) {
        return prev;
      }
      const [moved] = blocks.splice(fromIndex, 1);
      blocks.splice(toIndex, 0, moved);
      return updated;
    });
  }, []);

  const reorderExerciseRun = useCallback((blockIndex: number, fromRunIndex: number, toRunIndex: number) => {
    setEditedWorkout((prev) => {
      if (!prev) return prev;
      const updated = cloneWorkout(prev);
      const block = updated.workoutBlocks[blockIndex];
      if (!block?.exerciseSets) return prev;
      const runs = computeExerciseRuns(block.exerciseSets);
      if (fromRunIndex < 0 || toRunIndex < 0 || fromRunIndex >= runs.length || toRunIndex >= runs.length) {
        return prev;
      }
      const fromRun = runs[fromRunIndex];
      const rows = block.exerciseSets.splice(fromRun.firstIndex, fromRun.indices.length);
      const targetRun = runs[toRunIndex];
      const insertAt = toRunIndex > fromRunIndex ? targetRun.lastIndex + 1 - rows.length : targetRun.firstIndex;
      block.exerciseSets.splice(insertAt, 0, ...rows);
      block.exerciseSets.forEach((s, i) => {
        s.order = i;
      });
      return updated;
    });
  }, []);

  const addSetToRun = useCallback((blockIndex: number, groupKey: string) => {
    const repExpandKey = `${blockIndex}-${groupKey}`;
    setEditedWorkout((prev) => {
      if (!prev) return prev;
      const updated = cloneWorkout(prev);
      const block = updated.workoutBlocks[blockIndex];
      if (!block?.exerciseSets) return prev;
      const runs = computeExerciseRuns(block.exerciseSets);
      const run = runs.find((r) => r.groupKey === groupKey);
      if (!run?.sets.length) return prev;
      const source = block.exerciseSets[run.lastIndex];
      if (!source) return prev;
      const newSet = {
        ...source,
        id: `temp-${Date.now()}-${Math.random()}`,
        sets: 1,
      } as any;
      block.exerciseSets.splice(run.lastIndex + 1, 0, newSet);
      const vary =
        expandedRepRuns.has(repExpandKey) ||
        (!forcedSameRuns.has(repExpandKey) && !areSetsUniform(run.sets) && run.sets.length > 1);
      if (!vary) {
        const src = block.exerciseSets[run.indices[0]];
        const newIdx = run.lastIndex + 1;
        const t = block.exerciseSets[newIdx];
        if (src && t) {
          t.rep = src.rep;
          (t as { repText?: string }).repText = (src as { repText?: string }).repText;
          t.durationSeconds = src.durationSeconds;
          t.restSeconds = src.restSeconds;
          (t as { weightKg?: number }).weightKg = (src as { weightKg?: number }).weightKg;
        }
      }
      block.exerciseSets.forEach((s, i) => {
        s.order = i;
      });
      return updated;
    });
  }, [expandedRepRuns, forcedSameRuns]);

  const removeSet = useCallback((blockIndex: number, setIndex: number) => {
    setEditedWorkout((prev) => {
      if (!prev) return prev;
      const updated = cloneWorkout(prev);
      const block = updated.workoutBlocks[blockIndex];
      if (!block?.exerciseSets) return prev;
      const key = rowGroupKeyForExerciseRun(block.exerciseSets[setIndex]);
      const runLen = block.exerciseSets.filter((s) => rowGroupKeyForExerciseRun(s) === key).length;
      if (runLen <= 1) return prev;
      block.exerciseSets.splice(setIndex, 1);
      block.exerciseSets.forEach((s, i) => {
        s.order = i;
      });
      return updated;
    });
  }, []);

  const getMeasureType = useCallback(
    (blockIndex: number, groupKey: string, firstSet: any): 'reps' | 'duration' => {
      const mKey = runMeasureKey(blockIndex, groupKey);
      return (
        exerciseMeasureType[mKey] ||
        (firstSet?.durationSeconds != null && firstSet.durationSeconds > 0 ? 'duration' : 'reps')
      );
    },
    [exerciseMeasureType],
  );

  const toggleMeasureType = useCallback((blockIndex: number, groupKey: string) => {
    setEditedWorkout((prev) => {
      if (!prev) return prev;
      const updated = cloneWorkout(prev);
      const block = updated.workoutBlocks[blockIndex];
      if (!block?.exerciseSets) return prev;
      const runs = computeExerciseRuns(block.exerciseSets);
      const run = runs.find((r) => r.groupKey === groupKey);
      if (!run) return prev;
      const mKey = runMeasureKey(blockIndex, groupKey);
      const first = run.sets[0];
      const current =
        exerciseMeasureType[mKey] ||
        (first?.durationSeconds != null && first.durationSeconds > 0 ? 'duration' : 'reps');
      const next = current === 'reps' ? 'duration' : 'reps';
      setExerciseMeasureType((p) => ({ ...p, [mKey]: next }));
      const durationStash = stashedDurationByRunRef.current[mKey];
      if (next === 'duration') {
        const repStash = run.indices.map((idx) => {
          const s = block.exerciseSets![idx];
          return {
            rep: s?.rep,
            repText: (s as { repText?: string })?.repText,
          };
        });
        setStashedRepsByRun((p) => ({ ...p, [mKey]: repStash }));
        run.indices.forEach((idx, i) => {
          const s = block.exerciseSets![idx];
          if (!s) return;
          s.rep = undefined;
          (s as { repText?: string }).repText = undefined;
          const savedDur = durationStash?.[i];
          if (savedDur != null && Number(savedDur) > 0) {
            s.durationSeconds = savedDur;
          } else if (s.durationSeconds == null || Number(s.durationSeconds) <= 0) {
            s.durationSeconds = 30;
          }
        });
      } else {
        stashedDurationByRunRef.current[mKey] = run.indices.map(
          (idx) => block.exerciseSets![idx]?.durationSeconds,
        );
        const repStash = stashedRepsByRun[mKey];
        run.indices.forEach((idx, i) => {
          const s = block.exerciseSets![idx];
          if (!s) return;
          s.durationSeconds = undefined;
          const saved = repStash?.[i];
          if (saved) {
            s.rep = saved.rep;
            (s as { repText?: string }).repText = saved.repText;
          } else {
            s.rep = undefined;
            (s as { repText?: string }).repText = undefined;
          }
        });
      }
      return updated;
    });
  }, [exerciseMeasureType, stashedRepsByRun]);

  const updateSetRepText = useCallback((blockIndex: number, setIndex: number, raw: string, blur = false) => {
    setEditedWorkout((prev) => {
      if (!prev) return prev;
      const updated = cloneWorkout(prev);
      const s = updated.workoutBlocks[blockIndex]?.exerciseSets?.[setIndex];
      if (!s) return prev;
      if (blur) {
        const normalized = normalizeRepsTextOnBlur(raw);
        if (normalized === '') {
          s.repText = undefined;
          s.rep = undefined;
        } else if (REPS_TEXT_FINAL_PATTERN.test(normalized)) {
          (s as any).repText = normalized;
          const firstNum = parseInt(normalized.split('-')[0], 10);
          s.rep = firstNum > 0 ? firstNum : undefined;
        }
        s.durationSeconds = undefined;
      } else {
        (s as any).repText = sanitizeRepsTextInput(raw);
      }
      return updated;
    });
  }, []);

  const getTimeFieldDisplay = useCallback(
    (
      kind: TimeFieldKind,
      blockIndex: number,
      setIndex: number,
      seconds: number | null | undefined,
    ): string => {
      const key = timeFieldDraftKey(kind, blockIndex, setIndex);
      if (Object.prototype.hasOwnProperty.call(timeFieldDrafts, key)) {
        return timeFieldDrafts[key];
      }
      return formatSecondsToMMSS(seconds);
    },
    [timeFieldDrafts],
  );

  const setTimeFieldDraft = useCallback(
    (kind: TimeFieldKind, blockIndex: number, setIndex: number, raw: string) => {
      const key = timeFieldDraftKey(kind, blockIndex, setIndex);
      const formatted = formatDurationInputWhileTyping(raw);
      setTimeFieldDrafts((p) => ({ ...p, [key]: formatted }));
    },
    [],
  );

  const commitTimeFieldDraft = useCallback(
    (kind: TimeFieldKind, blockIndex: number, setIndices: number[]) => {
      if (!setIndices.length) return;
      const key = timeFieldDraftKey(kind, blockIndex, setIndices[0]);
      const raw = timeFieldDrafts[key];
      setTimeFieldDrafts((p) => {
        const next = { ...p };
        delete next[key];
        return next;
      });
      if (raw === undefined) return;
      const normalized = formatDurationInputWhileTyping(raw);
      const val = parseDurationInputToSeconds(normalized);
      if (val === null) return;
      setEditedWorkout((prev) => {
        if (!prev) return prev;
        const updated = cloneWorkout(prev);
        setIndices.forEach((setIndex) => {
          const s = updated.workoutBlocks[blockIndex]?.exerciseSets?.[setIndex];
          if (!s) return;
          if (kind === 'duration') {
            s.durationSeconds = val;
            s.rep = undefined;
            (s as { repText?: string }).repText = undefined;
          } else {
            s.restSeconds = val ?? 0;
          }
        });
        return updated;
      });
    },
    [timeFieldDrafts],
  );

  const updateSetWeight = useCallback((blockIndex: number, setIndex: number, raw: string) => {
    setEditedWorkout((prev) => {
      if (!prev) return prev;
      const updated = cloneWorkout(prev);
      const s = updated.workoutBlocks[blockIndex]?.exerciseSets?.[setIndex] as any;
      if (!s) return prev;
      s.weightKg = raw === '' ? undefined : parseFloat(raw);
      return updated;
    });
  }, []);

  const isRunVaryBySet = useCallback(
    (blockIndex: number, groupKey: string, sets: any[]) => {
      const repExpandKey = `${blockIndex}-${groupKey}`;
      if (expandedRepRuns.has(repExpandKey)) return true;
      if (forcedSameRuns.has(repExpandKey)) return false;
      return !areSetsUniform(sets) && sets.length > 1;
    },
    [expandedRepRuns, forcedSameRuns],
  );

  const setRunVariation = useCallback((blockIndex: number, groupKey: string, mode: 'same' | 'vary') => {
    const repExpandKey = `${blockIndex}-${groupKey}`;
    if (mode === 'vary') {
      setExpandedRepRuns((prev) => new Set(prev).add(repExpandKey));
      setForcedSameRuns((prev) => {
        const n = new Set(prev);
        n.delete(repExpandKey);
        return n;
      });
    } else {
      setExpandedRepRuns((prev) => {
        const n = new Set(prev);
        n.delete(repExpandKey);
        return n;
      });
      setForcedSameRuns((prev) => new Set(prev).add(repExpandKey));
      setEditedWorkout((prev) => {
        if (!prev) return prev;
        const updated = cloneWorkout(prev);
        const block = updated.workoutBlocks[blockIndex];
        if (!block?.exerciseSets) return prev;
        const runs = computeExerciseRuns(block.exerciseSets);
        const run = runs.find((r) => r.groupKey === groupKey);
        if (!run || !run.sets.length) return prev;
        const src = block.exerciseSets[run.indices[0]];
        run.indices.forEach((idx, i) => {
          if (i === 0) return;
          const t = block.exerciseSets![idx];
          t.rep = src.rep;
          (t as any).repText = (src as any).repText;
          t.durationSeconds = src.durationSeconds;
          t.restSeconds = src.restSeconds;
          (t as any).weightKg = (src as any).weightKg;
        });
        return updated;
      });
    }
  }, []);

  const publish = useCallback(async (): Promise<boolean> => {
    if (mode !== 'create' || !editedWorkout) return false;
    setSaveError(null);
    setNameError(null);

    const nameTrimmed = (editedWorkout.name || '').trim();
    if (!nameTrimmed) {
      setNameError('Workout name is required.');
      return false;
    }

    if (!editedWorkout.difficulty) {
      setSaveError({ title: 'Level required', message: 'Select a difficulty level.' });
      return false;
    }

    if (
      editedWorkout.estimatedDurationMinutes == null ||
      editedWorkout.estimatedDurationMinutes < 1
    ) {
      setSaveError({ title: 'Duration required', message: 'Enter workout duration in minutes.' });
      return false;
    }

    const bannerUrl = (editedWorkout.bannerImageUrl || '').trim();
    if (!bannerUrl || !isAllowlistedWorkoutBannerUrl(bannerUrl)) {
      setSaveError({
        title: 'Cover image required',
        message: 'Choose a cover image from the gallery.',
      });
      return false;
    }

    if (checkWorkoutNameExists?.(nameTrimmed)) {
      setNameError(
        `A workout with the name "${nameTrimmed}" already exists. Please choose a different name.`,
      );
      return false;
    }

    const noteTrim = (editedWorkout.note || '').trim();
    const blocksAfterFilter = (editedWorkout.workoutBlocks ?? []).filter((b, idx) => {
      const id = b?.id || `${idx}`;
      return !pendingBlockSetupIds.has(id);
    });
    const hasBlocksGraph = blocksAfterFilter.some(
      (b) => (b.exerciseSets ?? []).some((s) => String(s?.exerciseId || '').trim() !== ''),
    );

    try {
      setIsSaving(true);
      const created = await WorkoutService.createWorkout({
        name: nameTrimmed,
        note: noteTrim === '' ? undefined : noteTrim,
        estimatedDurationMinutes: editedWorkout.estimatedDurationMinutes,
        difficulty: editedWorkout.difficulty,
        bannerImageUrl: bannerUrl || undefined,
        isPublic: false,
      });

      let result = created;
      if (hasBlocksGraph) {
        const withId: WorkoutWithBlocks = { ...editedWorkout, id: created.id, name: nameTrimmed };
        const { payload, nameError: ne, bannerError: be } = sanitizeWorkoutForSave(withId, {
          pendingBlockSetupIds,
          bannerOptional: true,
        });
        if (ne) {
          setNameError(ne);
          return false;
        }
        if (be) {
          setSaveError({ title: 'Cover image required', message: be });
          return false;
        }
        result = await WorkoutService.updateWorkout(created.id, payload);
        WorkoutService.invalidateWorkoutCache(created.id);
      }

      setInitialSnapshot(cloneWorkout(result));
      setEditedWorkout(cloneWorkout(result));
      setSaveError(null);
      try {
        await onCreated?.(result);
      } catch (callbackErr) {
        console.error('Workout created but post-create callback failed:', messageFromUnknownError(callbackErr));
      }
      return true;
    } catch (e) {
      const rawMsg = messageFromUnknownError(e);
      if (rawMsg.toLowerCase().includes('already exists') || rawMsg.toLowerCase().includes('gallery image')) {
        if (rawMsg.toLowerCase().includes('gallery image')) {
          setSaveError({ title: 'Invalid cover image', message: rawMsg });
        } else {
          setNameError(rawMsg);
        }
      } else {
        const friendly = parseUpsertTrainerWorkoutError(e);
        setSaveError({ title: "Couldn't create workout", message: friendly });
      }
      return false;
    } finally {
      setIsSaving(false);
    }
  }, [mode, editedWorkout, pendingBlockSetupIds, checkWorkoutNameExists, onCreated]);

  const save = useCallback(async (): Promise<boolean> => {
    if (mode === 'create') return publish();
    if (!editedWorkout?.id) return false;
    setSaveError(null);
    setNameError(null);
    const { payload, nameError: ne, bannerError: be } = sanitizeWorkoutForSave(editedWorkout, {
      pendingBlockSetupIds,
    });
    if (ne) {
      setNameError(ne);
      return false;
    }
    if (be) {
      setSaveError({ title: 'Cover image required', message: be });
      return false;
    }
    if (checkWorkoutNameExists?.(payload.name, payload.id)) {
      setNameError(`A workout with the name "${payload.name}" already exists. Please choose a different name.`);
      return false;
    }
    const applySavedWorkout = async (saved: WorkoutWithBlocks) => {
      setInitialSnapshot(cloneWorkout(saved));
      setEditedWorkout(cloneWorkout(saved));
      setSaveError(null);
      try {
        await onSaved(saved);
      } catch (callbackErr) {
        console.error('Workout saved but post-save callback failed:', messageFromUnknownError(callbackErr));
      }
    };

    try {
      setIsSaving(true);
      const saved = await WorkoutService.updateWorkout(payload.id, payload);
      WorkoutService.invalidateWorkoutCache(payload.id);
      await applySavedWorkout(saved);
      return true;
    } catch (e) {
      try {
        WorkoutService.invalidateWorkoutCache(payload.id);
        const refetched = await WorkoutService.getWorkoutById(payload.id);
        if (refetched && workoutPersistedMatchesPayload(payload, refetched)) {
          await applySavedWorkout(refetched);
          return true;
        }
      } catch {
        /* save likely failed */
      }

      const rawMsg = messageFromUnknownError(e);
      if (rawMsg.toLowerCase().includes('already exists')) {
        setNameError(rawMsg);
      } else {
        const friendly = parseUpsertTrainerWorkoutError(e);
        setSaveError({ title: "Couldn't save workout", message: friendly });
      }
      return false;
    } finally {
      setIsSaving(false);
    }
  }, [mode, editedWorkout, pendingBlockSetupIds, checkWorkoutNameExists, onSaved, publish]);

  const blocks = editedWorkout?.workoutBlocks ?? [];

  const finalizePendingBlock = useCallback((blockIndex: number) => {
    setEditedWorkout((prev) => {
      if (!prev) return prev;
      const updated = cloneWorkout(prev);
      const block = updated.workoutBlocks[blockIndex];
      if (!block) return prev;
      const bid = block.id || `${blockIndex}`;
      if (!pendingBlockSetupIds.has(bid)) return prev;
      if (!pendingBlockShowsAddExerciseButton(block)) return prev;
      setPendingBlockSetupIds((p) => {
        const n = new Set(p);
        n.delete(bid);
        return n;
      });
      setBlockTypeLockedIds((p) => new Set(p).add(bid));
      return updated;
    });
  }, [pendingBlockSetupIds]);

  return {
    editedWorkout,
    setEditedWorkout,
    initialSnapshot,
    blocks,
    blockSets,
    exerciseMeasureType,
    pendingBlockSetupIds,
    blockTypeLockedIds,
    expandedRepRuns,
    forcedSameRuns,
    isRunVaryBySet,
    dirtyCount,
    isDirty,
    isSaving,
    saveError,
    nameError,
    nameErrorSeq,
    discard,
    save,
    publish,
    updateField,
    updateBlock,
    updateBlockSetsUi,
    updateBlockSetsCount,
    updateBlockNote,
    updateBlockType,
    duplicateBlock,
    deleteBlock,
    addBlockFromModal,
    addExercisesToBlock,
    deleteExerciseRun,
    duplicateExerciseRun,
    reorderBlocks,
    reorderExerciseRun,
    addSetToRun,
    removeSet,
    getMeasureType,
    toggleMeasureType,
    updateSetRepText,
    getTimeFieldDisplay,
    setTimeFieldDraft,
    commitTimeFieldDraft,
    updateSetWeight,
    setRunVariation,
    finalizePendingBlock,
    hasUnsavedChanges: () => hasUnsavedChanges(initialSnapshot, editedWorkout, pendingBlockSetupIds),
    formatSecondsToMMSS,
  };
}
